/**
 * Container component that renders the appropriate integration form
 * based on the connected integration type (QuickBooks or Xero).
 *
 * This component serves as a router/switch that:
 * - Checks the linked integration from TagLevelIntegration state
 * - Renders InvoiceQbo for QuickBooks integrations
 * - Renders InvoiceXero for Xero integrations
 * - Shows a message when no integration is connected
 */
import React from "react";
import { useSelector } from "react-redux";
import { Alert } from "react-bootstrap";
import { ReduxState } from "src/redux";
import { SUPPORTED_SCOPES } from "src/redux/reducers/TagLevelIntegration";
import { Invoice2, TransactionLine } from "src/docuclipper/DocuclipperTypes";
import InvoiceQbo from "./InvoiceQbo";
import InvoiceXero from "./InvoiceXero";
import InvoiceDynamics from "./InvoiceDynamics";
import InvoiceSage from "./InvoiceSage";
import { DYNAMICS_SAGE_INTEGRATIONS_ENABLED } from "../Integrations/IntegrationsTable";

type Props = {
  invoice: Invoice2;
  getMarginTop: (
    index: number,
    lineItem: any,
    margin: string,
    defaultMargin: string
  ) => string;
  tl: TransactionLine;
};

/**
 * Renders the appropriate invoice form based on the connected integration type.
 *
 * The component automatically determines which form to show based on
 * the linkedIntegrationDetails.scope from the TagLevelIntegration state.
 */
const InvoiceIntegrationForm: React.FC<Props> = ({
  invoice,
  getMarginTop,
  tl,
}) => {
  const { linkedIntegration, linkedIntegrationDetails, loading } = useSelector(
    (state: ReduxState) => ({
      linkedIntegration: state.TagLevelIntegration.linkedIntegration,
      linkedIntegrationDetails: state.TagLevelIntegration.linkedIntegrationDetails,
      loading: state.TagLevelIntegration.loading,
    })
  );

  // Determine integration type
  const isQuickBooks =
    linkedIntegrationDetails?.scope === SUPPORTED_SCOPES.quickbooks;
  const isXero = linkedIntegrationDetails?.scope === SUPPORTED_SCOPES.xero;
  // Gated OFF until the Dynamics/Sage connectors are credentialed + verified.
  const isDynamics =
    DYNAMICS_SAGE_INTEGRATIONS_ENABLED &&
    linkedIntegrationDetails?.scope === SUPPORTED_SCOPES.dynamics;
  const isSage =
    DYNAMICS_SAGE_INTEGRATIONS_ENABLED &&
    linkedIntegrationDetails?.scope === SUPPORTED_SCOPES.sage;

  // Show loading state while checking integration
  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border spinner-border-sm text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
        <span className="ml-2">Loading integration...</span>
      </div>
    );
  }

  // No integration connected - this shouldn't normally happen as InvoiceContainer
  // should handle this case, but we include it for safety
  if (!linkedIntegration || !linkedIntegrationDetails) {
    return (
      <Alert variant="info">
        <Alert.Heading>No Integration Connected</Alert.Heading>
        <p>
          Connect a QuickBooks or Xero integration to import invoices. You can
          connect an integration from the "Export & Sync" section on the right
          sidebar.
        </p>
      </Alert>
    );
  }

  // Render Xero form
  if (isXero) {
    return (
      <InvoiceXero invoice={invoice} getMarginTop={getMarginTop} tl={tl} />
    );
  }

  // Render Dynamics 365 Business Central form
  if (isDynamics) {
    return (
      <InvoiceDynamics invoice={invoice} getMarginTop={getMarginTop} tl={tl} />
    );
  }

  // Render Sage Business Cloud Accounting form
  if (isSage) {
    return (
      <InvoiceSage invoice={invoice} getMarginTop={getMarginTop} tl={tl} />
    );
  }

  // Render QuickBooks form (default)
  if (isQuickBooks) {
    return (
      <InvoiceQbo invoice={invoice} getMarginTop={getMarginTop} tl={tl} />
    );
  }

  // Unknown integration type - show a fallback message
  return (
    <Alert variant="warning">
      <Alert.Heading>Unsupported Integration</Alert.Heading>
      <p>
        The connected integration type ({linkedIntegrationDetails?.scope}) is
        not currently supported for invoice import.
      </p>
    </Alert>
  );
};

export default InvoiceIntegrationForm;

